import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { Card, CardContent, Typography, Box, Checkbox } from "@mui/material";
import { format } from "date-fns";

const DraggableMatchCard = ({
  match,
  isSelected,
  onSelect,
  showCheckbox = true,
  isCompact = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: match.id,
      data: match,
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const formatMatchTime = (dateString) => {
    if (!dateString) return "TBD";
    try {
      return format(new Date(dateString), "MMM dd, HH:mm");
    } catch {
      return "TBD";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "fixture":
        return "Upcoming";
      case "playing":
        return "Live";
      case "played":
        return "Finished";
      default:
        return status;
    }
  };

  if (isCompact) {
    return (
      <Card
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        sx={{
          mb: 1,
          cursor: isDragging ? "grabbing" : "grab",
          opacity: isDragging ? 0.5 : 1,
          border: isSelected ? "1px solid #1976D2" : "1px solid #e0e0e0",
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {showCheckbox && (
              <Checkbox
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelect(match.id);
                }}
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">
                  {match.homeContestantName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mx: 1 }}
                >
                  vs
                </Typography>
                <Typography variant="body2">
                  {match.awayContestantName}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {formatMatchTime(match.kickoffAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getStatusLabel(match.status)}
                </Typography>
                {match.status === "played" && (
                  <Typography variant="caption">
                    {match.homeScore} - {match.awayScore}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        mb: 2,
        cursor: isDragging ? "grabbing" : "grab",
        opacity: isDragging ? 0.5 : 1,
        border: isSelected ? "1px solid #1976D2" : "1px solid #e0e0e0",
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          {showCheckbox && (
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect(match.id);
              }}
              sx={{ mt: 0.5 }}
              onPointerDown={(e) => e.stopPropagation()}
            />
          )}

          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="h6">{match.homeContestantName}</Typography>
              <Typography variant="body1" color="text.secondary">
                VS
              </Typography>
              <Typography variant="h6">{match.awayContestantName}</Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {formatMatchTime(match.kickoffAt)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {getStatusLabel(match.status)}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {match.optaFixtureId}
              </Typography>

              {match.status === "played" && (
                <Typography variant="body2">
                  {match.homeScore} - {match.awayScore}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DraggableMatchCard;
